import { useEffect, useState } from 'react';

import { NativeSelect } from '@/components/ui/select';
import { HeaderContent, HEADER_TITLE_CLASS } from '../../components/shell/app-header';
import { api } from '../../lib/api';
import { useSearchParams } from '../../lib/routing';
import { HgSousThemeSelecteur } from './hg-college-sous-theme-selecteur';
import {
  CHOIX_JE_NE_SAIS_PAS,
  HgQcmExerciceVue,
  type HgQcmExerciceRow,
  type HgQcmTentativeResultRow,
} from './hg-college-qcm-vue';
import {
  HgCarteExerciceVue,
  JE_NE_SAIS_PAS_CARTE,
  type HgCarteExerciceRow,
  type HgCarteTentativeResultRow,
  type ReponseCarte,
} from './hg-college-carte-exercice-vue';
import {
  HgAnalyseDocExerciceVue,
  type HgAnalyseDocExerciceRow,
  type HgAnalyseDocTentativeResultRow,
  type ReponseAnalyseDoc,
} from './hg-college-analyse-doc-vue';
import {
  HgRedactionExerciceVue,
  type HgRedactionExerciceRow,
  type HgRedactionTentativeResultRow,
} from './hg-college-redaction-vue';
import type { HgNiveauMaitrise } from './hg-college-qcm-vue';

/* Les quatre niveaux du collège, dans l'ordre de la scolarité et non
   alphabétique : « 3e, 4e, 5e, 6e » se lirait à rebours d'une progression. */
const NIVEAUX_COLLEGE = ['6e', '5e', '4e', '3e'] as const;

/* Une série est un QCM, une carte ou une analyse de document : le
   discriminant `famille` vient du serveur (chaque mapper le pose en dur, voir
   toHgQcmExerciceRow / toHgCarteExerciceRow) et permet de fusionner les deux
   flux en UNE SEULE progression, sans que l'élève change de chemin de
   navigation selon le format sur lequel il tombe. */
type Serie =
  | { famille: 'qcm'; row: HgQcmExerciceRow }
  | { famille: 'carte_croquis'; row: HgCarteExerciceRow }
  | { famille: 'analyse_document'; row: HgAnalyseDocExerciceRow }
  | { famille: 'redaction_argumentee'; row: HgRedactionExerciceRow };

function trierSeries(series: Serie[]): Serie[] {
  return [...series].sort((a, b) => {
    const parCode = a.row.fiche_code.localeCompare(b.row.fiche_code);
    return parCode !== 0 ? parCode : a.row.position - b.row.position;
  });
}

export default function HgCollegeQcmApp() {
  const [searchParams] = useSearchParams();
  /* Le niveau est un ÉTAT, plus seulement un paramètre d'URL. Il ne l'était pas,
     et le contenu des quatre niveaux restait donc inatteignable sans réécrire
     l'adresse à la main : douze fiches en base, une seule accessible. */
  const [niveauCode, setNiveauCode] = useState<string>(
    () => searchParams.get('niveauCode') ?? '6e',
  );
  const [sousThemeId, setSousThemeId] = useState<string | null>(
    () => searchParams.get('sousThemeId'),
  );

  /* Changer de niveau invalide le sous-thème choisi : il appartient à l'ancien
     niveau et ne figure pas dans la nouvelle liste. Le garder afficherait des
     questions d'un niveau tandis que le sélecteur en annonce un autre. */
  const changerNiveau = (code: string) => {
    setNiveauCode(code);
    setSousThemeId(null);
    setSeries(null);
    setQcmResults({});
    setCarteResults({});
    setAnalyseDocResults({});
    setRedactionResults({});
    setQcmAnswers({});
    setCarteAnswers({});
    setAnalyseDocAnswers({});
    setAnalyseDocAuto({});
    setRedactionTexte({});
    setRedactionAuto({});
    setIndex(0);
  };

  const [series, setSeries] = useState<Serie[] | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [index, setIndex] = useState(0);
  const [qcmAnswers, setQcmAnswers] = useState<Record<string, string | null>>({});
  const [carteAnswers, setCarteAnswers] = useState<Record<string, ReponseCarte | undefined>>({});
  const [qcmResults, setQcmResults] = useState<Record<string, HgQcmTentativeResultRow>>({});
  const [carteResults, setCarteResults] = useState<Record<string, HgCarteTentativeResultRow>>({});
  /* L'analyse de document se répond par exercice, pas par item : un document
     choisi et un texte. Les deux autres familles indexent par item, d'où un
     état séparé plutôt qu'un élargissement forcé des leurs. */
  const [analyseDocAnswers, setAnalyseDocAnswers] = useState<Record<string, ReponseAnalyseDoc>>({});
  const [analyseDocAuto, setAnalyseDocAuto] = useState<Record<string, HgNiveauMaitrise | null>>({});
  const [analyseDocResults, setAnalyseDocResults] = useState<
    Record<string, HgAnalyseDocTentativeResultRow>
  >({});
  /* La rédaction argumentée se répond par exercice, comme l'analyse de
     document, mais avec PLUSIEURS critères auto-positionnés au lieu d'un
     seul — d'où un état à deux niveaux (exercice → critère → niveau). */
  const [redactionTexte, setRedactionTexte] = useState<Record<string, string>>({});
  const [redactionAuto, setRedactionAuto] = useState<
    Record<string, Record<string, HgNiveauMaitrise | undefined>>
  >({});
  const [redactionResults, setRedactionResults] = useState<
    Record<string, HgRedactionTentativeResultRow>
  >({});
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  useEffect(() => {
    if (!sousThemeId) return;
    let cancelled = false;
    setSeries(null);
    setLoadError(null);
    setIndex(0);
    void (async () => {
      try {
        const [qcm, carte, analyseDoc, redaction] = await Promise.all([
          api.miniApps.hgCollege.qcm.list.query({ sousThemeId }),
          api.miniApps.hgCollege.carte.list.query({ sousThemeId }),
          api.miniApps.hgCollege.analyseDoc.list.query({ sousThemeId }),
          api.miniApps.hgCollege.redaction.list.query({ sousThemeId }),
        ]);
        if (cancelled) return;
        setSeries(
          trierSeries([
            ...qcm.map((row): Serie => ({ famille: 'qcm', row })),
            ...carte.map((row): Serie => ({ famille: 'carte_croquis', row })),
            ...analyseDoc.map((row): Serie => ({ famille: 'analyse_document', row })),
            ...redaction.map((row): Serie => ({ famille: 'redaction_argumentee', row })),
          ]),
        );
      } catch (e) {
        if (!cancelled) setLoadError(e instanceof Error ? e.message : String(e));
      }
    })();
    return () => { cancelled = true; };
  }, [sousThemeId]);

  const current = series?.[index] ?? null;

  useEffect(() => {
    setQcmAnswers({});
    setCarteAnswers({});
    setSubmitError(null);
  }, [current?.row.id]);

  const resultatDe = (serie: Serie) =>
    serie.famille === 'qcm'
      ? qcmResults[serie.row.id]
      : serie.famille === 'carte_croquis'
        ? carteResults[serie.row.id]
        : serie.famille === 'analyse_document'
          ? analyseDocResults[serie.row.id]
          : redactionResults[serie.row.id];

  const currentResult = current ? resultatDe(current) : undefined;

  const annonceChargement = !sousThemeId || loadError
    ? ''
    : series === null
      ? 'Chargement des questions…'
      : series.length === 0
        ? 'Aucune question disponible pour ce sous-thème.'
        : '';

  const submit = async () => {
    if (!current) return;
    setSubmitting(true);
    setSubmitError(null);
    try {
      if (current.famille === 'qcm') {
        const row = current.row;
        const result = await api.miniApps.hgCollege.qcm.submit.mutate({
          exerciceId: row.id,
          reponses: row.items.map((item) => {
            const choisi = qcmAnswers[item.id] ?? null;
            const jeNeSaisPas = choisi === CHOIX_JE_NE_SAIS_PAS;
            return { itemId: item.id, choixId: jeNeSaisPas ? null : choisi, jeNeSaisPas };
          }),
        });
        setQcmResults((prev) => ({ ...prev, [row.id]: result }));
      } else if (current.famille === 'carte_croquis') {
        const row = current.row;
        const result = await api.miniApps.hgCollege.carte.submit.mutate({
          exerciceId: row.id,
          reponses: row.items.map((item) => {
            const valeur = carteAnswers[item.id];
            const jeNeSaisPas = valeur === JE_NE_SAIS_PAS_CARTE;
            const point = valeur && !jeNeSaisPas ? valeur : null;
            return { itemId: item.id, point, jeNeSaisPas };
          }),
        });
        setCarteResults((prev) => ({ ...prev, [row.id]: result }));
      } else if (current.famille === 'analyse_document') {
        const row = current.row;
        const reponse = analyseDocAnswers[row.id] ?? { documentId: null, texte: null };
        const itemChoix = row.items.find((item) => item.type === 'choix_document');
        const itemJustification = row.items.find((item) => item.type === 'justification');
        const result = await api.miniApps.hgCollege.analyseDoc.submit.mutate({
          exerciceId: row.id,
          reponses: [
            ...(itemChoix ? [{ itemId: itemChoix.id, documentId: reponse.documentId }] : []),
            ...(itemJustification ? [{ itemId: itemJustification.id, texte: reponse.texte }] : []),
          ],
          autoPositionnement: analyseDocAuto[row.id] ?? null,
        });
        setAnalyseDocResults((prev) => ({ ...prev, [row.id]: result }));
      } else {
        const row = current.row;
        const texte = redactionTexte[row.id] ?? '';
        const autoParCritere = redactionAuto[row.id] ?? {};
        const result = await api.miniApps.hgCollege.redaction.submit.mutate({
          exerciceId: row.id,
          texte,
          autoPositionnements: row.criteres
            .filter((critere) => autoParCritere[critere.id] !== undefined)
            .map((critere) => ({ critereId: critere.id, niveau: autoParCritere[critere.id]! })),
        });
        setRedactionResults((prev) => ({ ...prev, [row.id]: result }));
      }
    } catch (e) {
      setSubmitError(e instanceof Error ? e.message : String(e));
    } finally {
      setSubmitting(false);
    }
  };

  const seriesFaites = series?.map((serie) => resultatDe(serie) !== undefined);
  /* L'analyse de document et la rédaction ne comptent pas leurs items comme
     des questions : une analyse est UNE tâche (deux facettes), une rédaction
     n'a pas d'`items[]` du tout — les additionner au dénominateur ferait
     passer un exercice pour plusieurs questions de QCM. */
  const totalQuestions = series?.reduce(
    (n, serie) =>
      n + (serie.famille === 'analyse_document' || serie.famille === 'redaction_argumentee'
        ? 0
        : serie.row.items.length),
    0,
  );

  return (
    <>
      <HeaderContent width="md" showBurger backTo="/mini-apps" backLabel="Mini-apps">
        <h1 className={HEADER_TITLE_CLASS}>Histoire-géographie et EMC</h1>
      </HeaderContent>
      <main className="max-w-[720px] mx-auto px-5 pb-16 pt-4">

        <div className="mb-5 flex flex-col gap-3">
          <div>
            <label htmlFor="hg-niveau" className="block text-sm font-medium mb-1">
              Niveau
            </label>
            <NativeSelect
              id="hg-niveau"
              value={niveauCode}
              onChange={(event) => changerNiveau(event.target.value)}
            >
              {NIVEAUX_COLLEGE.map((code) => (
                <option key={code} value={code}>
                  {code}
                </option>
              ))}
            </NativeSelect>
          </div>

          <HgSousThemeSelecteur
            key={niveauCode}
            niveauCode={niveauCode}
            valeur={sousThemeId}
            onChange={setSousThemeId}
          />
        </div>

        <p role="status" className="sr-only">{annonceChargement}</p>

        {sousThemeId && loadError && <p role="alert" className="text-destructive text-sm">{loadError}</p>}

        {sousThemeId && !loadError && series === null && (
          <p className="text-muted-foreground">Chargement…</p>
        )}

        {sousThemeId && !loadError && series !== null && series.length === 0 && (
          <p className="text-muted-foreground text-sm">Aucune question disponible pour ce sous-thème pour le moment.</p>
        )}

        {current && series && current.famille === 'qcm' && (
          <HgQcmExerciceVue
            exercice={current.row}
            index={index}
            total={series.length}
            seriesFaites={seriesFaites}
            totalQuestions={totalQuestions}
            answers={qcmAnswers}
            result={currentResult as HgQcmTentativeResultRow | undefined}
            submitting={submitting}
            submitError={submitError}
            onAnswer={(itemId, choixId) => setQcmAnswers((prev) => ({ ...prev, [itemId]: choixId }))}
            onPrecedent={() => setIndex((i) => Math.max(0, i - 1))}
            onSuivant={() => setIndex((i) => i + 1)}
            onValider={submit}
          />
        )}

        {current && series && current.famille === 'carte_croquis' && (
          <HgCarteExerciceVue
            exercice={current.row}
            index={index}
            total={series.length}
            seriesFaites={seriesFaites}
            totalQuestions={totalQuestions}
            answers={carteAnswers}
            result={currentResult as HgCarteTentativeResultRow | undefined}
            submitting={submitting}
            submitError={submitError}
            onAnswer={(itemId, reponse) => setCarteAnswers((prev) => ({ ...prev, [itemId]: reponse }))}
            onPrecedent={() => setIndex((i) => Math.max(0, i - 1))}
            onSuivant={() => setIndex((i) => i + 1)}
            onValider={submit}
          />
        )}

        {current && series && current.famille === 'analyse_document' && (
          <HgAnalyseDocExerciceVue
            exercice={current.row}
            index={index}
            total={series.length}
            seriesFaites={seriesFaites}
            reponse={analyseDocAnswers[current.row.id] ?? { documentId: null, texte: null }}
            autoPositionnement={analyseDocAuto[current.row.id] ?? null}
            result={currentResult as HgAnalyseDocTentativeResultRow | undefined}
            submitting={submitting}
            submitError={submitError}
            onChoisirDocument={(documentId) =>
              setAnalyseDocAnswers((prev) => ({
                ...prev,
                [current.row.id]: { ...(prev[current.row.id] ?? { documentId: null, texte: null }), documentId },
              }))
            }
            onEcrire={(texte) =>
              setAnalyseDocAnswers((prev) => ({
                ...prev,
                [current.row.id]: { ...(prev[current.row.id] ?? { documentId: null, texte: null }), texte },
              }))
            }
            onAutoPositionner={(niveau) =>
              setAnalyseDocAuto((prev) => ({ ...prev, [current.row.id]: niveau }))
            }
            onPrecedent={() => setIndex((i) => Math.max(0, i - 1))}
            onSuivant={() => setIndex((i) => i + 1)}
            onValider={submit}
          />
        )}

        {current && series && current.famille === 'redaction_argumentee' && (
          <HgRedactionExerciceVue
            exercice={current.row}
            index={index}
            total={series.length}
            seriesFaites={seriesFaites}
            texte={redactionTexte[current.row.id] ?? ''}
            autoPositionnements={redactionAuto[current.row.id] ?? {}}
            result={currentResult as HgRedactionTentativeResultRow | undefined}
            submitting={submitting}
            submitError={submitError}
            onEcrire={(texte) =>
              setRedactionTexte((prev) => ({ ...prev, [current.row.id]: texte }))
            }
            onAutoPositionner={(critereId, niveau) =>
              setRedactionAuto((prev) => ({
                ...prev,
                [current.row.id]: { ...(prev[current.row.id] ?? {}), [critereId]: niveau },
              }))
            }
            onPrecedent={() => setIndex((i) => Math.max(0, i - 1))}
            onSuivant={() => setIndex((i) => i + 1)}
            onValider={submit}
          />
        )}
      </main>
    </>
  );
}
